import { PBEnums } from "~/types/pb/enums";

/**
 * An object that defines the priority levels for various roles within a system.
 * The keys represent role names, and the values indicate their corresponding priority levels.
 * Lower numerical values signify higher priority.
 *
 * This structure can be used to determine hierarchical access or permissions based on priority values.
 */
const rolesPriority: Record<string, number> = {
	Owner: 0,
	Admin: 1,
	Other: 2,
	None: 999
};

/**
 * An object mapping roles to their respective numeric scores.
 * The `rolesScores` variable assigns a specific score to each role defined in the `PBEnums.Roles` enumeration.
 * These scores are used to represent role hierarchy or precedence, where lower scores generally indicate higher priority or permissions.
 *
 * The keys are enumerated roles from `PBEnums.Roles`, while the values are numeric scores.
 *
 * @type {Record<PBEnums.Roles, number>}
 */
const rolesScores: Record<PBEnums.Roles, number> = {
	[PBEnums.Roles.Owner]: rolesPriority.Owner,
	[PBEnums.Roles.Admin]: rolesPriority.Admin,
	[PBEnums.Roles.Invoice]: rolesPriority.Other,
	[PBEnums.Roles.Stats]: rolesPriority.Other,
	[PBEnums.Roles.S3ReadOnly]: rolesPriority.Other,
	[PBEnums.Roles.None]: rolesPriority.None
};

/**
 * Retrieves the role corresponding to the given name.
 *
 * @param {string} name - The name of the role to be retrieved.
 * @return {PBEnums.Roles} The corresponding role from PBEnums.Roles, or PBEnums.Roles.None if no matching role is found.
 */
export function getRoleFromName (name: string): PBEnums.Roles {
	switch (name) {
	case "owner":
		return PBEnums.Roles.Owner;
	case "admin":
		return PBEnums.Roles.Admin;
	case "invoice":
		return PBEnums.Roles.Invoice;
	case "stat":
		return PBEnums.Roles.Stats;
	case "object-storage-read-only":
		return PBEnums.Roles.S3ReadOnly;
	default:
		return PBEnums.Roles.None;
	}
}

/**
 * Retrieves the score associated with a given role.
 *
 * @param {PBEnums.Roles} role - The role whose score needs to be determined.
 * @return {number} The score corresponding to the provided role. Defaults to 999 if no score is found.
 */
export function getRoleScore (role: PBEnums.Roles): number {
	// eslint-disable-next-line no-magic-numbers
	return rolesScores[role] ?? 999;
}
